import React, { ReactElement, useContext } from 'react'
import styled from 'styled-components'
import { map } from 'lodash'
import { SVGImage } from '~components/homepage/svg/SVGImage'
import { KlosySVGId } from '~components/homepage/svg/types/svg'

import { Article as ArticleType } from '~src/types/article'
import LanguageContext from '~components/homepage/context/language-context'
import {
  Divider,
  Info,
  List,
  ListItem,
  Paragraph,
  Picture,
  SourceLink,
  StyledDot,
  Subtitle,
  Title,
} from './styled'

const ArticleContainer = styled.div`
  max-width: 670px;
  margin: 0 auto;
`

interface Props {
  article: ArticleType
}

const Article: React.FC<Props> = ({ article }): ReactElement => {
  const { lang } = useContext(LanguageContext)

  const currentArticle = lang === 'EN' && article.en ? article.en : article
  const { title, mainPicture, content } = currentArticle || {}

  return (
    <ArticleContainer>
      <div>
        <Title>{title}</Title>
        <Picture asImage {...mainPicture} />
        {map(
          content,
          (contentUnit, index): ReactElement => {
            const t = contentUnit.type

            if (t === 'p')
              return (
                <Paragraph
                  key={`${lang}-${t}-${contentUnit.content.slice(0, 30)}`}
                >
                  {contentUnit.content}
                </Paragraph>
              )

            if (t === 'subtitle')
              return (
                <Subtitle
                  key={`${lang}-${t}-${contentUnit.content.slice(0, 30)}`}
                >
                  {contentUnit.content}
                </Subtitle>
              )

            if (t === 'subtitleGray')
              return (
                <Subtitle
                  gray
                  key={`${lang}-${t}-${contentUnit.content.slice(0, 30)}`}
                >
                  {contentUnit.content}
                </Subtitle>
              )

            if (t === 'img')
              return <Picture asImage key={`${t}-${index}`} {...contentUnit} />

            if (t === 'info')
              return (
                <Info key={`${lang}-${t}-${contentUnit.content.slice(0, 30)}`}>
                  {contentUnit.content}
                </Info>
              )

            if (t === 'sourceLink')
              return (
                <SourceLink
                  key={`${lang}-${t}-${contentUnit.href}`}
                  {...contentUnit}
                />
              )

            if (t === 'divider')
              return <Divider key={`${lang}-${t}-divider`} {...contentUnit} />

            if (t === 'list')
              return (
                <List
                  key={`${lang}-${t}-${contentUnit.items
                    .join('')
                    .slice(0, 30)}`}
                >
                  {contentUnit.items.map(item => (
                    <ListItem key={`${lang}-${t}-${item.slice(0, 20)}`}>
                      {item}
                    </ListItem>
                  ))}
                </List>
              )

            return <div></div>
          }
        )}
        <StyledDot>
          <SVGImage
            svgProps={[KlosySVGId.slideMarker, 'klosy-dot-splitter', 13, 13]}
          />
        </StyledDot>
      </div>
    </ArticleContainer>
  )
}

export default Article
